import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  FileText, Download, TrendingUp, ShieldCheck, AlertTriangle,
  Activity, Loader2, BarChart3, PieChart as PieChartIcon, Target
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0f172a', '#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#06b6d4', '#84cc16'];

export default function AdvancedReportingPage() {
  const [execSummary, setExecSummary] = useState(null);
  const [complianceReport, setComplianceReport] = useState(null);
  const [riskReport, setRiskReport] = useState(null);
  const [auditReport, setAuditReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchAllReports();
  }, []);

  const fetchAllReports = async () => {
    try {
      const token = localStorage.getItem('grc_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [exec, comp, risk, audit] = await Promise.all([
        axios.get(`${API}/api/reports/executive-summary`, { headers }),
        axios.get(`${API}/api/reports/compliance`, { headers }),
        axios.get(`${API}/api/reports/risk`, { headers }),
        axios.get(`${API}/api/reports/audit-trail`, { headers }),
      ]);
      setExecSummary(exec.data);
      setComplianceReport(comp.data);
      setRiskReport(risk.data);
      setAuditReport(audit.data);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('grc_token');
      const response = await axios.get(`${API}/api/reports/compliance/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `zentry_audit_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (err) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  const compSummary = complianceReport?.summary || {};
  const riskSummary = riskReport?.summary || {};
  const health = execSummary?.overall_health || 'Unknown';

  // Chart data
  const compliancePieData = [
    { name: 'Compliant', value: compSummary.compliant || 0 },
    { name: 'Gap', value: compSummary.gap || 0 },
    { name: 'Pending', value: compSummary.pending || 0 },
  ].filter(d => d.value > 0);

  const riskCategoryData = Object.entries(riskReport?.by_category || {}).map(([name, data]) => ({
    name: name.length > 15 ? name.slice(0, 15) + '..' : name,
    count: data.count,
    avgScore: data.count > 0 ? Math.round(data.total_score / data.count) : 0,
  }));

  const auditByAction = Object.entries(auditReport?.by_action || {})
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  return (
    <DashboardLayout>
      <div className="space-y-6 px-1" data-testid="advanced-reporting-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Advanced Reporting</h1>
            <p className="text-sm text-slate-500 mt-1">Comprehensive analytics and exportable audit reports</p>
          </div>
          <Button onClick={handleDownloadPDF} disabled={downloading} data-testid="download-pdf-btn">
            {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Export Full PDF
          </Button>
        </div>

        {/* Executive Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Overall Health', value: health, icon: Activity,
              color: health === 'Good' ? 'text-emerald-600' : 'text-amber-600',
              bg: health === 'Good' ? 'bg-emerald-50' : 'bg-amber-50' },
            { label: 'Compliance Rate', value: `${execSummary?.compliance?.compliance_rate || 0}%`, icon: ShieldCheck,
              color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Open Risks', value: riskSummary.open_risks || 0, icon: AlertTriangle,
              color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Total Controls', value: compSummary.total_controls || 0, icon: Target,
              color: 'text-slate-700', bg: 'bg-slate-50' },
          ].map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.label} className={`border-0 ${card.bg}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={16} className={card.color} />
                    <span className="text-xs text-slate-500">{card.label}</span>
                  </div>
                  <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue="compliance">
          <TabsList>
            <TabsTrigger value="compliance" data-testid="tab-compliance-report">
              <ShieldCheck size={14} className="mr-1.5" /> Compliance
            </TabsTrigger>
            <TabsTrigger value="risk" data-testid="tab-risk-report">
              <AlertTriangle size={14} className="mr-1.5" /> Risk
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit-report">
              <FileText size={14} className="mr-1.5" /> Audit Trail
            </TabsTrigger>
          </TabsList>

          {/* Compliance Tab */}
          <TabsContent value="compliance" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Compliance Pie Chart */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <PieChartIcon size={16} /> Control Status Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {compliancePieData.length > 0 ? (
                    <div style={{ width: '100%', height: 250 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={compliancePieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                            outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                            {compliancePieData.map((_, i) => (
                              <Cell key={i} fill={['#059669', '#dc2626', '#d97706'][i]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">No control data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Compliance Stats */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-base">Compliance Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { label: 'Compliant', value: compSummary.compliant, total: compSummary.total_controls, color: 'bg-emerald-500' },
                      { label: 'Gap', value: compSummary.gap, total: compSummary.total_controls, color: 'bg-red-500' },
                      { label: 'Pending', value: compSummary.pending, total: compSummary.total_controls, color: 'bg-amber-500' },
                    ].map((item) => (
                      <div key={item.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">{item.label}</span>
                          <span className="font-medium">{item.value || 0} / {item.total || 0}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div className={`h-2 rounded-full ${item.color}`}
                            style={{ width: `${item.total ? (item.value / item.total * 100) : 0}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Risk Tab */}
          <TabsContent value="risk" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Risk by Category */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 size={16} /> Risks by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {riskCategoryData.length > 0 ? (
                    <div style={{ width: '100%', height: 280 }}>
                      <ResponsiveContainer>
                        <BarChart data={riskCategoryData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#0f172a" radius={[4, 4, 0, 0]} name="Count" />
                          <Bar dataKey="avgScore" fill="#2563eb" radius={[4, 4, 0, 0]} name="Avg Score" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">No risk data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Risks */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-base">Top Risks by Score</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(riskReport?.top_risks || []).slice(0, 6).map((risk, i) => (
                      <div key={risk.id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{risk.title || risk.name}</p>
                          <p className="text-xs text-slate-500">{risk.category || 'Uncategorized'}</p>
                        </div>
                        <Badge className={
                          risk.risk_score >= 20 ? 'bg-red-100 text-red-800' :
                          risk.risk_score >= 15 ? 'bg-amber-100 text-amber-800' :
                          'bg-blue-100 text-blue-800'
                        }>
                          Score: {risk.risk_score || 0}
                        </Badge>
                      </div>
                    ))}
                    {(!riskReport?.top_risks || riskReport.top_risks.length === 0) && (
                      <p className="text-sm text-slate-500 text-center py-4">No risks registered yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Audit Trail Tab */}
          <TabsContent value="audit" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Audit Actions Chart */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 size={16} /> Actions Distribution
                  </CardTitle>
                  <CardDescription>{auditReport?.total_entries || 0} total audit entries</CardDescription>
                </CardHeader>
                <CardContent>
                  {auditByAction.length > 0 ? (
                    <div style={{ width: '100%', height: 280 }}>
                      <ResponsiveContainer>
                        <BarChart data={auditByAction} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tick={{ fontSize: 10 }} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={100} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#0f172a" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">No audit data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Audit by User */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-base">Activity by User</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(auditReport?.by_user || {}).sort(([,a],[,b]) => b - a).slice(0, 8).map(([email, count]) => (
                      <div key={email} className="flex items-center justify-between">
                        <span className="text-sm text-slate-700 truncate max-w-[200px]">{email}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-slate-100 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full bg-blue-500"
                              style={{ width: `${Math.min(100, (count / (auditReport?.total_entries || 1)) * 100 * 3)}%` }} />
                          </div>
                          <span className="text-sm font-medium text-slate-900 w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                    {Object.keys(auditReport?.by_user || {}).length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">No activity recorded yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Entries */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">Recent Audit Entries</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-slate-500 font-medium">Action</th>
                        <th className="text-left py-2 px-3 text-slate-500 font-medium">User</th>
                        <th className="text-left py-2 px-3 text-slate-500 font-medium">Entity</th>
                        <th className="text-left py-2 px-3 text-slate-500 font-medium">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(auditReport?.recent_entries || []).slice(0, 15).map((entry, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="py-2 px-3 font-medium">{entry.action}</td>
                          <td className="py-2 px-3 text-slate-600">{entry.user_email || 'System'}</td>
                          <td className="py-2 px-3 text-slate-500">{entry.entity_type}:{(entry.entity_id || '').slice(0, 8)}</td>
                          <td className="py-2 px-3 text-slate-400 text-xs">{entry.timestamp?.slice(0, 19)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!auditReport?.recent_entries || auditReport.recent_entries.length === 0) && (
                    <p className="text-sm text-slate-500 text-center py-6">No audit entries yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
